import { Loading } from 'components/index.js'
import React, { useEffect, useRef, useState } from 'react'
import { formatDate } from 'utils/formatUtils'
import { notifyToast } from 'utils/responseUtils'
import { useDispatch } from 'react-redux'
import { selectModelOption } from 'config/static-data/news-tab-data'
import Link from 'next/link'
import { getChangedSentiment, getChangedStyle, getNewsNarration, getNewsSummary } from 'store/actions/news.action'
import ReactAudioPlayer from 'react-audio-player'
import CreatableSelect from 'react-select/creatable'
import { useMediaQuery } from 'react-responsive'
import { useSwipeable } from 'react-swipeable'
import { Share2 } from 'lucide-react'
import { ShareButton } from '@components/ShareButton/ShareButton'

const initialSentimentOptions = [
  { value: 'angry', label: 'Angry' },
  { value: 'happy', label: 'Happy' },
  { value: 'cheerful', label: 'Cheerful' },
  { value: 'doubtful', label: 'Doubtful' },
]

const initialStyleOptions = [
  { value: 'j.k. rowling', label: 'J.k. rowling' },
  { value: 'stephen king', label: 'Stephen king' },
  { value: 'george rr martin', label: 'George rr martin' },
  { value: 'agatha christie', label: 'Agatha christie' },
  { value: 'j.r.r. tolkien', label: 'J.r.r. tolkien' },
]

const NewsContent = ({
  selectedNews,
  currentIndex,
  filteredNewsData,
  handleNextButtonClick,
  handlePrevButtonClick,
  handleNewsLike,
  isLiked,
  isBookmarked,
  totalLikes,
  totalBookmarks,
  handleBookmark,
}) => {
  const dispatch = useDispatch()
  const contentRef = useRef()
  const isMobile = useMediaQuery({ maxWidth: 600 })
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1023 })

  const [narratedAudio, setNarratedAudio] = useState(null)
  const [selectedModelOption, setSelectedModelOption] = useState('')
  const [selectedSentiment, setSelectedSentiment] = useState(null)
  const [initialItems, setInitialItems] = useState(initialSentimentOptions)
  const [styleOptions, setStyleOptions] = useState(initialStyleOptions)
  const [aiTransformedContent, setAiTransformedContent] = useState()
  const [selectedStyle, setSelectedStyle] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [toggleModels, setToggleModels] = useState(false)

  const getInnerText = () => {
    const contentRefElement = contentRef.current
    if (contentRefElement) {
      const innerText = contentRefElement.innerText
      return innerText
    } else {
      return ''
    }
  }

  const handleSummarizeNews = async (content) => {
    try {
      setIsLoading(true)
      setError(false)
      const data = await getNewsSummary(content)
      if (data?.summary) {
        setAiTransformedContent(data?.summary)
      }
      setIsLoading(false)
      setError(false)
    } catch (err) {
      dispatch(notifyToast('error', err?.message))
      setIsLoading(false)
      setError(err)
    }
  }

  const handleNewsNarration = async (content) => {
    try {
      setIsLoading(true)
      setError(false)
      const audioUrl = await getNewsNarration(content)
      if (audioUrl) {
        setNarratedAudio(audioUrl)
      }
      setIsLoading(false)
      setError(false)
    } catch (err) {
      dispatch(notifyToast('error', err?.message))
      setIsLoading(false)
      setError(err)
    }
  }

  const handleClearModels = () => {
    setAiTransformedContent('')
    setNarratedAudio(null)
    setSelectedStyle(null)
    setSelectedSentiment(null)
    setSelectedModelOption('')
  }

  const handleSentimentSelect = async (sentiment) => {
    const content = getInnerText()
    try {
      setIsLoading(true)
      setError(false)
      const newContent = await getChangedSentiment(content, sentiment)
      newContent && typeof newContent === 'string' && setAiTransformedContent(newContent)
      setIsLoading(false)
      setError(false)
    } catch (err) {
      dispatch(notifyToast('error', err?.message))
      setIsLoading(false)
      setError(err)
    }
  }

  const handleStyleSelect = async (style) => {
    const content = getInnerText()
    try {
      setIsLoading(true)
      setError(false)
      const data = await getChangedStyle(content, style)
      data && typeof data === 'string' && setAiTransformedContent(data)
      setIsLoading(false)
      setError(false)
    } catch (err) {
      dispatch(notifyToast('error', err?.message))
      setIsLoading(false)
      setError(err)
    }
  }

  useEffect(() => {
    if (!selectModelOption) {
      setAiTransformedContent('')
      setNarratedAudio(null)
    }
  }, [selectedModelOption])

  useEffect(() => {
    const { value } = selectedSentiment || {}
    value && handleSentimentSelect(value)
  }, [selectedSentiment?.value])

  useEffect(() => {
    const { value } = selectedStyle || {}
    value && handleStyleSelect(value)
  }, [selectedStyle?.value])

  const config = {
    delta: 10,
    preventScrollOnSwipe: false,
    trackTouch: true,
    trackMouse: false,
    rotationAngle: 0,
    swipeDuration: Infinity,
    touchEventOptions: { passive: true },
  }

  const onSwipedLeft = () => {
    if (currentIndex > 0) {
      handlePrevButtonClick()
    }
  }

  const onSwipedRight = () => {
    if (currentIndex < filteredNewsData?.length) {
      handleNextButtonClick()
    }
  }

  const handlers = useSwipeable({
    onSwipedLeft,
    onSwipedRight,
    ...config,
  })

  return (
    <div {...handlers} className="flex flex-col lg:flex-row px-2 md:px-4 h-[90vh] max-w-7xl mx-auto">
      {/* Article content */}
      <div className="news-wrapper w-full lg:w-2/3 xl:w-3/4 space-y-3 md:space-y-4 h-full overflow-y-hidden">
        <div className="news-header pb-2 pt-0 md:py-3 flex justify-between items-center flex-row relative gap-2 md:gap-3 my-2 md:my-0">
          <div className="flex gap-3 md:gap-5 items-center w-full justify-around md:justify-between">
            <button
              className={`w-10 ${
                currentIndex == 0 ? 'opacity-40 !pointer-events-none !cursor-not-allowed' : 'opacity-100'
              }`}
              onClick={() => handlePrevButtonClick()}
            >
              <img
                src={currentIndex == 0 ? '/assets/common/left-chevron.svg' : '/assets/common/left-chevron-fill.svg'}
                className={`w-5 h-5 md:w-8 md:h-8`}
                alt="arrow"
              />
            </button>

            <div className="flex items-center space-x-3 md:space-x-6 bg-white p-1.5 md:p-2 rounded-full">
              <button
                className="flex items-center space-x-1 text-gray-500 hover:text-blue-600 transition duration-200"
                onClick={handleNewsLike}
              >
                <img
                  src={`/assets/common/thumbs-up${isLiked ? '-fill' : ''}.svg`}
                  className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6"
                  alt="like"
                />
                <span className="font-semibold text-sm md:text-base">
                  {totalLikes > 1000 ? `${(totalLikes / 1000).toFixed(2)}k` : totalLikes} {!isMobile && 'Like'}
                </span>
              </button>

              <button
                className="flex items-center space-x-1 text-gray-500 hover:text-black transition duration-200"
                onClick={handleBookmark}
              >
                <img
                  src={`/assets/common/news-bookmark${isBookmarked ? '-fill' : ''}.svg`}
                  className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6"
                  alt="bookmark"
                />
                <span className="font-semibold text-sm md:text-base">
                  {totalBookmarks > 1000 ? `${(totalBookmarks / 1000).toFixed(2)}k` : totalBookmarks}
                </span>
              </button>

              <ShareButton
                shareUrl={window.location.href}
                title={selectedNews?.title}
                description={selectedNews?.full_description ? Object.values(selectedNews.full_description)[0]?.[0] : ''}
                className="flex items-center space-x-1 text-gray-500 hover:text-gray-800 transition duration-200"
              >
                <Share2 size={isMobile ? 16 : 20} className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                <span className="font-semibold text-sm md:text-base">36</span>
              </ShareButton>
            </div>
            <button
              className={`w-10 ${
                currentIndex && currentIndex == filteredNewsData?.length - 1
                  ? 'opacity-40 !pointer-events-none !cursor-not-allowed'
                  : 'opacity-100'
              }`}
              onClick={() => handleNextButtonClick()}
            >
              <img
                src={
                  currentIndex && currentIndex == filteredNewsData?.length - 1
                    ? '/assets/common/right-chevron.svg'
                    : '/assets/common/right-chevron-fill.svg'
                }
                className={`w-5 h-5 md:w-8 md:h-8`}
                alt="arrow"
              />
            </button>
          </div>
        </div>
        <div className="flex main-content-news flex-col max-h-[calc(90vh-100px)] overflow-y-auto pr-1 md:pr-2 scrollbar-thin-vertical">
          <div className="flex flex-col">
            <div className="news-detail-content w-full flex flex-col gap-2 md:gap-3 py-2 md:py-3 lg:pr-4 xl:pr-5 mt-0">
              {selectedNews?.category?.length > 0 && (
                <div className="flex flex-row gap-5 gap-y-1 mt-1 md:mb-2 font-medium  flex-wrap">
                  {typeof selectedNews?.category == 'string' ? (
                    <span className=" text-[#000] md:bg-[#F9F9F9] font-medium text-xs md:text-sm md:px-[9px] md:py-[3px]  capitalize ">
                      {selectedNews?.category}
                    </span>
                  ) : (
                    selectedNews?.category?.slice(0, 3)?.map((tag, i) => {
                      return (
                        <span
                          key={i}
                          className="md:bg-[#F9F9F9] md:px-[9px] md:py-[3px] text-[#000] font-normal text-xs md:text-sm  capitalize"
                        >
                          {tag}
                        </span>
                      )
                    })
                  )}
                </div>
              )}

              {/* Tag */}

              {/* 
              <p className="text-red-500 font-bold uppercase text-sm">Environment</p>
              */}

              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold leading-tight">{selectedNews?.title}</h2>
              {/* Mobile/Tablet Image - only show on smaller screens */}
              {(selectedNews?.media?.thumbnail?.url ||
                selectedNews?.imagesArr?.length > 0 ||
                selectedNews?.images_url_arr?.length > 0) && (
                <img
                  key={currentIndex}
                  src={
                    selectedNews?.media?.thumbnail?.url ||
                    (selectedNews?.imagesArr?.length && selectedNews?.imagesArr[0]) ||
                    (selectedNews?.images_url_arr?.length && selectedNews?.images_url_arr[0])
                  }
                  className="w-full h-48 md:h-56 object-cover object-top lg:hidden rounded-lg"
                  alt="thumbnail"
                />
              )}
              <div className="card-footer flex justify-between items-center">
                <span className="font-medium  text-xs md:texxt-sm text-[#807d7d] tracking-[0.02em]">
                  {selectedNews?.author} - {formatDate(selectedNews?.published)}
                </span>
                <div className="flex md:gap-6">
                  <span className="font-medium  text-xs md:texxt-sm text-[#807d7d] tracking-[0.02em]">
                    Views - {selectedNews?.view_count}
                  </span>
                </div>
              </div>
              {isLoading && !error && <Loading />}
              <div
                className={` text-neutral-700  ${aiTransformedContent && 'select-text selection:bg-yellow-400'}`}
                ref={contentRef}
              >
                {narratedAudio && selectedModelOption == 'narrate' && (
                  <ReactAudioPlayer src={narratedAudio} controls className="w-full" />
                )}

                {aiTransformedContent
                  ? aiTransformedContent
                  : selectedNews?.full_description &&
                    Object.entries(selectedNews?.full_description)?.length > 0 &&
                    Object?.entries(selectedNews?.full_description)?.map(([section, paragraphs], sectionIndex) => (
                      <div key={sectionIndex}>
                        {section?.toLowerCase() !== 'introduction' && (
                          <h2 className="text-2xl mt-2 font-bold mb-0 md:mt-[16px] tracking-[0.02em] ">{section}</h2>
                        )}

                        {paragraphs?.map((paragraph, paragraphIndex) => (
                          <p
                            key={paragraphIndex}
                            className="mt-1 md:mt-3 text-sm md:text-base lg:text-lg text-[#333] tracking-[0.02em] font-normal "
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    ))}
              </div>

              <p className="bg-neutral-200 rounded-md px-4 py-1 mt-3 text-xs font-semibold mb-2">
                Read full article at :{' '}
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={selectedNews?.link}
                  className="text-blue-700 font-normal underline"
                >
                  {selectedNews?.link}{' '}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Author section */}
      <div className="flex flex-col lg:w-1/3 xl:w-1/4 mt-4 lg:mt-6 gap-3 md:gap-4 lg:items-center items-start lg:ml-4 xl:ml-6">
        {/* Author Info */}
        <div className="lg:flex hidden flex-col items-center">
          <img
            src="/assets/common/dummy-profile-pic.png" // Placeholder image for the author
            alt="author"
            className="w-16 h-16 rounded-full"
          />
          <div className="text-center mt-1">
            <p className="font-semibold text-lg">{selectedNews?.author}</p>
            <p className="text-gray-500">{formatDate(selectedNews?.published)}</p>
          </div>
        </div>

        {/* Article Image */}
        {(selectedNews?.media?.thumbnail?.url ||
          selectedNews?.imagesArr?.length > 0 ||
          selectedNews?.images_url_arr?.length > 0) && (
          <div className="w-full hidden lg:block">
            <img
              key={currentIndex}
              src={
                selectedNews?.media?.thumbnail?.url ||
                (selectedNews?.imagesArr?.length && selectedNews?.imagesArr[0]) ||
                (selectedNews?.images_url_arr?.length && selectedNews?.images_url_arr[0])
              }
              className="w-full h-48 md:h-56 lg:h-60 xl:h-64 object-cover object-top rounded-lg"
              alt="thumbnail"
            />
          </div>
        )}

        <div className="right-news-content relative flex w-full flex-col justify-start">
          <div className="fixed lg:static right-4 md:right-6 bottom-40 lg:bottom-auto z-10">
            <div
              className={`flex-col gap-2 md:gap-3 py-3 lg:py-4 px-3 lg:px-4 bg-[#f6f6f6] rounded-lg ${isTabletOrMobile ? (toggleModels ? '!flex' : '!hidden') : '!flex'}`}
            >
              <div className="w-full">
                <button
                  className={`group border-0 rounded-md py-2 md:py-2.5 px-3 md:px-4 text-xs md:text-sm font-medium flex justify-start gap-2 items-center w-full hover:bg-black hover:text-white transition-colors
${selectedModelOption == 'summarize' && 'bg-black text-white'}
`}
                  onClick={() => {
                    setSelectedModelOption('summarize')
                    handleSummarizeNews(getInnerText())
                  }}
                >
                  <img
                    src="/assets/common/summarize-icon.svg"
                    alt="summarize"
                    className={`group-hover:invert-[1] w-4 h-4 md:w-5 md:h-5 ${selectedModelOption == 'summarize' && 'invert-[1]'}`}
                  />
                  Summarize
                </button>
              </div>

              <div className="w-full">
                <button
                  className={`group border-0 rounded-md py-2 md:py-2.5 px-3 md:px-4 text-xs md:text-sm font-medium flex justify-start gap-2 items-center w-full hover:bg-black hover:text-white transition-colors
${selectedModelOption == 'narrate' && 'bg-black text-white'}
`}
                  onClick={() => {
                    setSelectedModelOption('narrate')
                    handleNewsNarration(getInnerText())
                  }}
                >
                  <img
                    src="/assets/common/narrate-icon.svg"
                    alt="narrate"
                    className={`group-hover:invert-[1] w-4 h-4 md:w-5 md:h-5 ${selectedModelOption == 'narrate' && 'invert-[1]'}`}
                  />
                  Narrate
                </button>
                <div className="w-full"></div>
              </div>
              <div className="w-full">
                <button
                  className={`group border-0 rounded-md py-2 md:py-2.5 px-3 md:px-4 text-xs md:text-sm font-medium flex justify-start gap-2 items-center w-full hover:bg-black hover:text-white transition-colors
${selectedModelOption == 'change-style' && 'bg-black text-white'}
`}
                  onClick={() => {
                    setSelectedModelOption('change-style')
                  }}
                >
                  <img
                    src="/assets/common/tone.svg"
                    alt="tone"
                    className={`group-hover:invert-[1] w-4 h-4 md:w-5 md:h-5 ${selectedModelOption == 'change-style' && 'invert-[1]'}`}
                  />
                  Style
                </button>
                {selectedModelOption == 'change-style' && (
                  <CreatableSelect
                    className="basic-single mt-3 rounded-md"
                    classNamePrefix="select"
                    value={selectedStyle}
                    isDisabled={false}
                    onCreateOption={(value) => {
                      setStyleOptions((prev) => [...prev, { value: value, label: value }])
                      setSelectedStyle({ value: value, label: value })
                    }}
                    isClearable={false}
                    styles={
                      isTabletOrMobile
                        ? {
                            marginTop: '30px',
                            menu: (provided) => ({
                              ...provided,
                              marginLeft: '-144px',
                              marginTop: '-150px',
                              height: '150px',
                              overflowY: 'scroll',
                            }),
                          }
                        : { marginTop: '30px' }
                    }
                    isSearchable={true}
                    name="style"
                    options={styleOptions}
                    onChange={(option) => setSelectedStyle(option)}
                  />
                )}
              </div>

              <div className="w-full">
                <button
                  className={`group border-0 rounded-md py-2 md:py-2.5 px-3 md:px-4 text-xs md:text-sm font-medium flex justify-start gap-2 items-center w-full hover:bg-black hover:text-white transition-colors
${selectedModelOption == 'change-sentiment' && 'bg-black text-white'}
`}
                  onClick={() => {
                    setSelectedModelOption('change-sentiment')
                  }}
                >
                  <img
                    src="/assets/common/bottles.svg"
                    alt="bottles"
                    className={`group-hover:invert-[1] w-4 h-4 md:w-5 md:h-5 ${selectedModelOption == 'change-sentiment' && 'invert-[1]'}`}
                  />
                  Sentiment
                </button>
                {selectedModelOption == 'change-sentiment' && (
                  <CreatableSelect
                    className="basic-single mt-3 rounded-md"
                    classNamePrefix="select"
                    value={selectedSentiment}
                    isDisabled={false}
                    onCreateOption={(value) => {
                      setInitialItems((prev) => [...prev, { value: value, label: value }])
                      setSelectedSentiment({ value: value, label: value })
                    }}
                    isClearable={false}
                    styles={
                      isTabletOrMobile
                        ? {
                            marginTop: '30px',
                            menu: (provided) => ({
                              ...provided,
                              marginLeft: '-144px',
                              marginTop: '-150px',
                              height: '150px',
                              overflowY: 'scroll',
                            }),
                          }
                        : { marginTop: '30px' }
                    }
                    isSearchable={true}
                    name="Home"
                    options={initialItems}
                    onChange={(option) => setSelectedSentiment(option)}
                  />
                )}
              </div>
            </div>
            <div className="hidden lg:block text-left mt-3">
              <button
                className={`font-medium text-xs py-2.5 px-3 w-full bg-neutral-950 text-white rounded-md ${!selectedModelOption && '!hidden '}`}
                onClick={handleClearModels}
              >
                Clear Models
              </button>
            </div>

            <div
              className="lg:hidden rounded-full bg-white p-[2px] text-right float-right shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => setToggleModels(!toggleModels)}
            >
              <img
                src={`/assets/common/${toggleModels ? 'cancle-gradient' : 'news-models-toggle'}.svg`}
                className="w-10 h-10 md:w-12 md:h-12"
                alt="modal toggle"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewsContent
